/**
 * Custom left-side menu drawer.
 *
 * Self-contained (does NOT reuse the native header menu drawer DOM or JS, and
 * does not depend on the theme's dialog.js). Opens from triggers that carry a
 * `data-custom-menu-open="<drawer-id>"` attribute via a single delegated
 * document listener, so it keeps working when the header section is
 * re-rendered by the Theme Editor.
 *
 * Behaviour: open / close, click overlay to close, Escape to close, background
 * scroll lock, and focus returned to the trigger on close.
 */
function isThemeEditorPreview() {
  return Boolean(window.Shopify?.designMode || document.documentElement.classList.contains('shopify-design-mode'));
}

class CustomMenuDrawer extends HTMLElement {
  /** @type {HTMLElement | null} */
  activeTrigger = null;
  /** @type {AbortController | null} */
  #controller = null;
  #isOpen = false;
  #scrollLocked = false;
  #suppressCollectionClick = false;
  #prevBodyOverflow = '';
  #prevBodyPaddingRight = '';

  connectedCallback() {
    this.overlay = this.querySelector('[data-cmd-overlay]');
    this.panel = this.querySelector('[data-cmd-panel]');
    this.closeButton = this.querySelector('[data-cmd-close]');

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.overlay?.addEventListener('click', () => this.close(), { signal });
    this.closeButton?.addEventListener('click', () => this.close(), { signal });

    // Keep the keydown handler local to the element lifecycle.
    this.handleKeydown = (event) => {
      if (event.key !== 'Escape' || !this.#isOpen) return;
      event.preventDefault();
      // Escape backs out one level at a time: close the deepest open sub-panel
      // (last in DOM order on the open path), then eventually the drawer itself.
      const openSubpanels = this.querySelectorAll('.custom-menu-drawer__subpanel.is-open');
      if (openSubpanels.length > 0) {
        this.#closeSubpanel(openSubpanels[openSubpanels.length - 1]);
      } else {
        this.close();
      }
    };
    document.addEventListener('keydown', this.handleKeydown, { signal });

    this.#bindSubpanels(signal);
    this.#bindCollectionsDrag(signal);
    this.#bindCollectionLinks(signal);
  }

  /**
   * Delegated open/close for reusable sub-panels inside the drawer.
   * Triggers: [data-cmd-subpanel-open="<name>"]; panels: [data-cmd-subpanel="<name>"];
   * back/close controls: [data-cmd-subpanel-close].
   * @param {AbortSignal} signal
   */
  #bindSubpanels(signal) {
    this.panel?.addEventListener(
      'click',
      (event) => {
        const target = event.target instanceof Element ? event.target : null;
        if (!target) return;

        const opener = target.closest('[data-cmd-subpanel-open]');
        if (opener && this.contains(opener)) {
          event.preventDefault();
          this.#openSubpanel(opener.getAttribute('data-cmd-subpanel-open'));
          return;
        }

        const closer = target.closest('[data-cmd-subpanel-close]');
        if (closer && this.contains(closer)) {
          event.preventDefault();
          this.#closeSubpanel(closer.closest('.custom-menu-drawer__subpanel'));
        }
      },
      { signal }
    );
  }

  /** @param {string | null} name */
  #openSubpanel(name) {
    if (!name) return;
    const subpanel = this.querySelector(`[data-cmd-subpanel="${name}"]`);
    if (!subpanel) return;
    subpanel.classList.add('is-open');
    subpanel.setAttribute('aria-hidden', 'false');
  }

  /** @param {Element | null} subpanel */
  #closeSubpanel(subpanel) {
    if (!subpanel) return;
    subpanel.classList.remove('is-open');
    subpanel.setAttribute('aria-hidden', 'true');
  }

  #closeAllSubpanels() {
    this.querySelectorAll('.custom-menu-drawer__subpanel.is-open').forEach((subpanel) =>
      this.#closeSubpanel(subpanel)
    );
  }

  /**
   * Mouse drag-to-scroll for the horizontal collections list (PC). Touch
   * devices keep native swipe scrolling, so this only engages for the mouse.
   * @param {AbortSignal} signal
   */
  #bindCollectionsDrag(signal) {
    const list = this.querySelector('.custom-menu-drawer__collections_nav_list');
    if (!list) return;

    list.classList.add('is-draggable');

    let isDown = false;
    let dragged = false;
    let startX = 0;
    let startScroll = 0;
    let clearDraggedTimer = null;
    const dragThreshold = 8;

    list.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerType !== 'mouse') return;
        if (event.button !== 0) return;
        if (list.scrollWidth - list.clientWidth <= 1) return;
        if (clearDraggedTimer) window.clearTimeout(clearDraggedTimer);
        isDown = true;
        dragged = false;
        startX = event.clientX;
        startScroll = list.scrollLeft;
      },
      { signal }
    );

    list.addEventListener(
      'pointermove',
      (event) => {
        if (!isDown) return;
        const dx = event.clientX - startX;
        if (!dragged && Math.abs(dx) > dragThreshold) {
          dragged = true;
          list.classList.add('is-dragging');
          if (!list.hasPointerCapture?.(event.pointerId)) {
            list.setPointerCapture(event.pointerId);
          }
        }
        if (dragged) list.scrollLeft = startScroll - dx;
      },
      { signal }
    );

    const end = (event) => {
      if (!isDown) return;
      isDown = false;
      if (event.pointerId != null && list.hasPointerCapture?.(event.pointerId)) {
        list.releasePointerCapture(event.pointerId);
      }
      // Drop the dragging state on the next frame so the click handler below
      // can still see it and suppress an accidental navigation.
      window.requestAnimationFrame(() => list.classList.remove('is-dragging'));
      if (dragged) this.#suppressCollectionClick = true;
      clearDraggedTimer = window.setTimeout(() => {
        dragged = false;
        this.#suppressCollectionClick = false;
        clearDraggedTimer = null;
      }, 100);
    };
    list.addEventListener('pointerup', end, { signal });
    list.addEventListener('pointercancel', end, { signal });
    list.addEventListener('lostpointercapture', end, { signal });

    // Cancel the click that follows a drag so links don't navigate.
    list.addEventListener(
      'click',
      (event) => {
        if (dragged) {
          event.preventDefault();
          event.stopPropagation();
          dragged = false;
          this.#suppressCollectionClick = false;
          if (clearDraggedTimer) {
            window.clearTimeout(clearDraggedTimer);
            clearDraggedTimer = null;
          }
        }
      },
      { capture: true, signal }
    );

    // Prevent the browser's native image/link drag ghost.
    list.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
  }

  /**
   * Force collection-card links to navigate in capture phase, before the
   * drawer/theme click handlers get a chance to swallow normal link clicks.
   * @param {AbortSignal} signal
   */
  #bindCollectionLinks(signal) {
    this.panel?.addEventListener(
      'click',
      (event) => {
        if (!(event instanceof MouseEvent) || event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        const target = event.target instanceof Element ? event.target : null;
        const link = target?.closest('a[data-cmd-collection-link]');
        if (!(link instanceof HTMLAnchorElement) || !this.contains(link)) return;
        if (!link.href || link.getAttribute('target') === '_blank') return;
        if (isThemeEditorPreview()) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.#suppressCollectionClick) {
          this.#suppressCollectionClick = false;
          return;
        }
        window.location.assign(link.href);
      },
      { capture: true, signal }
    );
  }

  disconnectedCallback() {
    // Restore scroll if the element is torn down while open (e.g. editor reload).
    if (this.#isOpen) this.#unlockScroll();
    this.#isOpen = false;
    this.#controller?.abort();
    this.#controller = null;
  }

  /** @param {HTMLElement | null} [trigger] */
  open(trigger = null) {
    if (this.#isOpen) return;
    this.#isOpen = true;
    this.activeTrigger = trigger;

    this.overlay?.removeAttribute('hidden');
    this.classList.add('is-open');
    this.panel?.setAttribute('aria-hidden', 'false');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');

    this.#lockScroll();

    // Move focus into the drawer for keyboard / screen-reader users.
    window.requestAnimationFrame(() => {
      this.closeButton?.focus();
    });
  }

  close() {
    if (!this.#isOpen) return;
    this.#isOpen = false;

    this.classList.remove('is-open');
    this.panel?.setAttribute('aria-hidden', 'true');
    if (this.activeTrigger) this.activeTrigger.setAttribute('aria-expanded', 'false');

    // Reset any open sub-panel so the drawer reopens at the top level.
    this.#closeAllSubpanels();

    this.#unlockScroll();

    // Return focus to whatever opened the drawer.
    const trigger = this.activeTrigger;
    this.activeTrigger = null;
    if (trigger && typeof trigger.focus === 'function') trigger.focus();

    // Hide the overlay after the close transition so it can't trap clicks.
    const overlay = this.overlay;
    if (overlay) {
      window.setTimeout(() => {
        if (!this.#isOpen) overlay.setAttribute('hidden', '');
      }, 300);
    }
  }

  #lockScroll() {
    if (this.#scrollLocked) return;
    this.#scrollLocked = true;
    const { body } = document;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    this.#prevBodyOverflow = body.style.overflow;
    this.#prevBodyPaddingRight = body.style.paddingRight;
    body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) body.style.paddingRight = `${scrollbarWidth}px`;
  }

  #unlockScroll() {
    if (!this.#scrollLocked) return;
    this.#scrollLocked = false;
    const { body } = document;
    body.style.overflow = this.#prevBodyOverflow;
    body.style.paddingRight = this.#prevBodyPaddingRight;
  }
}

if (!customElements.get('custom-menu-drawer')) {
  customElements.define('custom-menu-drawer', CustomMenuDrawer);
}

// Theme Editor hot reload does not replace an already-registered custom
// element class, so keep link navigation delegated outside the class as well.
if (!window.__customMenuDrawerCollectionNavigation) {
  window.__customMenuDrawerCollectionNavigation = true;
  let collectionPointerStart = null;
  let collectionPointerLink = null;
  let collectionPointerMoved = false;

  const getCollectionLink = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const link = target?.closest('a[data-cmd-collection-link]');
    return link instanceof HTMLAnchorElement && link.closest('#CustomMenuDrawer') ? link : null;
  };

  document.addEventListener(
    'pointerdown',
    (event) => {
      if (isThemeEditorPreview()) return;
      const link = getCollectionLink(event);
      if (!link || event.button !== 0) return;
      collectionPointerStart = { x: event.clientX, y: event.clientY };
      collectionPointerLink = link;
      collectionPointerMoved = false;
    },
    true
  );

  document.addEventListener(
    'pointermove',
    (event) => {
      if (!collectionPointerStart) return;
      const dx = event.clientX - collectionPointerStart.x;
      const dy = event.clientY - collectionPointerStart.y;
      if (Math.hypot(dx, dy) > 8) collectionPointerMoved = true;
    },
    true
  );

  document.addEventListener(
    'pointerup',
    (event) => {
      const link = getCollectionLink(event) || collectionPointerLink;
      if (!link) return;
      if (isThemeEditorPreview()) {
        collectionPointerStart = null;
        collectionPointerLink = null;
        collectionPointerMoved = false;
        return;
      }
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      if (collectionPointerMoved) {
        collectionPointerStart = null;
        collectionPointerLink = null;
        collectionPointerMoved = false;
        return;
      }

      collectionPointerStart = null;
      collectionPointerLink = null;
      collectionPointerMoved = false;
      window.location.href = link.href;
    },
    true
  );

  document.addEventListener(
    'pointercancel',
    () => {
      collectionPointerStart = null;
      collectionPointerLink = null;
      collectionPointerMoved = false;
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const link = getCollectionLink(event);
      if (!link || !(event instanceof MouseEvent) || event.button !== 0) return;
      if (isThemeEditorPreview()) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      if (collectionPointerMoved) {
        collectionPointerStart = null;
        collectionPointerLink = null;
        collectionPointerMoved = false;
        return;
      }

      collectionPointerLink = null;
      window.location.assign(link.href);
    },
    true
  );
}

// One-time delegated trigger handler. Survives header section re-renders because
// it is attached to the document rather than to a specific button instance.
if (!window.__customMenuDrawerDelegated) {
  window.__customMenuDrawerDelegated = true;
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const trigger = target?.closest('[data-custom-menu-open]');
    if (!trigger) return;

    const id = trigger.getAttribute('data-custom-menu-open') || 'CustomMenuDrawer';
    const drawer = document.getElementById(id);
    if (drawer && typeof drawer.open === 'function') {
      event.preventDefault();
      drawer.open(trigger);
    }
  });
}
